/**
 * TasteTestQuestions (TTQ / 品题) domain contracts
 *
 * 术语（v0.7+）：
 * - TTQ：品题产品域
 * - TTQA（Taste Test Question Activity）：品题活动 — 持久化工作单元；存储层沿用 `ttq_sessions`，类型别名 `Ttqa`
 * - TTQIB（TTQ InfiniBoard）：品题无限板 — 画布 UI 状态，归属 TTQA.ttqibState
 * - CTTQ（ConvergingTTQ）：收敛品题 — 多聚合网络、动态复习入口（resolveOptimalReviewEntry）
 * - TTQP（TtqaProduct）：品题课程产出物
 * - TTQAngle：品题角度 — L1 三一三分法 + L2 GO-ON 子变式
 *
 * 设计文档：
 * - PsycheFlow-AppFunctor/docs/ttq-angle-model.md
 * - PsycheFlow-AppFunctor/docs/ttqa-ttqib-domain.md
 * - PsycheFlow-AppFunctor/docs/cttq-domain.md
 * - PsycheFlow-AppFunctor/docs/ttq-interactive-flow.md
 * - PsycheFlow-AppFunctor/docs/ttq-workbench-api.md
 */
// ---------------------------------------------------------------------------
// 交互式品题引导 & TTQP（品题课程产出物）
// 设计文档：PsycheFlow-AppFunctor/docs/ttq-interactive-flow.md
// ---------------------------------------------------------------------------
export const TTQ_QUESTION_CHAIN_VARIANT_IDS = [
    'question_chain',
    'condition_generalize',
    'condition_specialize',
    'conclusion_progression',
    'inverse_swap',
    'context_transfer',
    'element_weak_strong',
    'depth_extend',
    'error_boundary',
    'custom',
];
export const TTQ_SANY_ANGLE_IDS = [
    'multi_solution',
    'question_chain',
    'pattern_unify',
];
export const TTQ_SANY_ANGLE_KIND = {
    multi_solution: 'one_to_many',
    question_chain: 'one_to_many',
    pattern_unify: 'many_to_one',
};
export function resolveSanyAngleFromPathId(pathId) {
    if (pathId === 'multi_solution')
        return 'multi_solution';
    if (pathId === 'pattern_unify')
        return 'pattern_unify';
    if (TTQ_QUESTION_CHAIN_VARIANT_IDS.includes(pathId)) {
        return 'question_chain';
    }
    return null;
}
export function resolveDefaultPathIdForSanyAngle(angleId) {
    return angleId;
}
export function isTtqChainAngle(angleId) {
    return angleId === 'question_chain' || angleId === 'questions_chain';
}
/** 题链 L1 + L2 → 执行 pathId */
export function resolveChainPathId(angleId, variantId) {
    return variantId;
}
// ---------------------------------------------------------------------------
// CTTQ（ConvergingTTQ / 收敛品题）
// 设计文档：PsycheFlow-AppFunctor/docs/cttq-domain.md
// ---------------------------------------------------------------------------
/** 复习替代生效的默认最低覆盖度阈值 */
export const CTTQ_REVIEW_SUBSTITUTION_THRESHOLD = 0.75;
/** 冗余检测：提示阈值（UI 黄标） */
export const CTTQ_REDUNDANCY_WARN_THRESHOLD = 0.55;
/** 冗余检测：默认降为 deprioritized */
export const CTTQ_REDUNDANCY_DEPRIORITIZE_THRESHOLD = 0.75;
/** deprioritized CTTQ 在 LEC 择优中的得分系数 */
export const CTTQ_DEPRIORITIZED_SCORE_FACTOR = 0.55;
/** portfolio：CTTQ/member 比率黄标 */
export const CTTQ_PORTFOLIO_RATIO_WARN = 0.8;
/** portfolio：CTTQ 数 ≥ member 数（红色 · 收敛失败） */
export const CTTQ_PORTFOLIO_RATIO_CRITICAL = 1.0;
export function emptyIpttqSkillSpec(partial) {
    return {
        mainTitle: String(partial?.mainTitle || '').trim() || '品题浓缩结果（草稿）',
        subtitle: partial?.subtitle ?? null,
        implicitCapabilities: Array.isArray(partial?.implicitCapabilities)
            ? partial.implicitCapabilities.map((item) => String(item || '').trim()).filter(Boolean)
            : [],
        implicitCapabilityBindings: Array.isArray(partial?.implicitCapabilityBindings)
            ? partial.implicitCapabilityBindings
            : (partial?.implicitCapabilityBindings ?? null),
        thinkingLineage: String(partial?.thinkingLineage || '').trim(),
        answerMustShow: Array.isArray(partial?.answerMustShow)
            ? partial.answerMustShow.map((item) => String(item || '').trim()).filter(Boolean)
            : [],
        forbiddenExplicitLabels: Array.isArray(partial?.forbiddenExplicitLabels)
            ? partial.forbiddenExplicitLabels.map((item) => String(item || '').trim()).filter(Boolean)
            : [],
        notes: partial?.notes ?? null,
        intuitionSketch: partial?.intuitionSketch ?? null,
        contentUpdatedAt: partial?.contentUpdatedAt != null
            ? String(partial.contentUpdatedAt).trim() || null
            : null,
        generatorModel: partial?.generatorModel != null
            ? String(partial.generatorModel).trim() || null
            : null,
        generatorProvider: partial?.generatorProvider != null
            ? String(partial.generatorProvider).trim() || null
            : null,
        schemaVersion: 1,
    };
}
/** skill 是否已有可用正文（非空壳草稿） */
export function ipttqHasSkillBody(skill) {
    if (!skill)
        return false;
    const caps = skill.implicitCapabilities || [];
    const lineage = String(skill.thinkingLineage || '').trim();
    const draftLineage = !lineage
        || lineage.includes('待根据')
        || lineage.includes('请稍后重试')
        || lineage.includes('由模型建议创建');
    if (caps.length > 0)
        return true;
    return !draftLineage && lineage.length >= 12;
}
/** ISK 是否已到位（ready + url 或 ossId）——看当前选用槽 */
export function ipttqIskIsReady(sketch) {
    const display = getIskDisplayImage(sketch);
    if (!display)
        return false;
    const status = display.status || sketch?.status;
    return Boolean(status === 'ready'
        && (String(display.imageUrl || '').trim() || String(display.imageOssId || '').trim()));
}
/** 槽是否有可用预览图（ready 或残留图） */
export function iskSlotHasImage(slot) {
    if (!slot)
        return false;
    return Boolean(String(slot.imageUrl || '').trim() || String(slot.imageOssId || '').trim());
}
/** 从顶层字段构造兼容 original 槽 */
export function legacyIskAsOriginalSlot(sketch) {
    if (!sketch)
        return null;
    const hasTop = Boolean(String(sketch.imageUrl || '').trim()
        || String(sketch.imageOssId || '').trim()
        || sketch.status === 'pending'
        || sketch.status === 'failed');
    if (!hasTop)
        return null;
    return {
        imageUrl: sketch.imageUrl ?? null,
        imageOssId: sketch.imageOssId ?? null,
        status: sketch.status,
        model: sketch.model ?? null,
        provider: sketch.provider ?? null,
        cost: Number.isFinite(Number(sketch.cost)) ? Number(sketch.cost) : null,
        generatedAt: sketch.generatedAt ?? null,
        errorMessage: sketch.errorMessage ?? null,
        providerJobId: sketch.providerJobId ?? null,
    };
}
/** 读取规范化后的三槽（无 slots 时顶层视为 original） */
export function getIskSlots(sketch) {
    const slots = sketch?.slots;
    const original = slots?.original ?? legacyIskAsOriginalSlot(sketch);
    return {
        original: original || null,
        handDrawn: slots?.handDrawn || null,
        repaired: slots?.repaired || null,
    };
}
const ISK_SLOT_PRIORITY = ['repaired', 'handDrawn', 'original'];
/** 解析选用槽：显式 selectedSlot（有图）优先，否则 repaired > handDrawn > original */
export function resolveIskSelectedSlot(sketch) {
    const slots = getIskSlots(sketch);
    const explicit = sketch?.selectedSlot;
    if (explicit && iskSlotHasImage(slots[explicit]))
        return explicit;
    for (const kind of ISK_SLOT_PRIORITY) {
        if (iskSlotHasImage(slots[kind]))
            return kind;
    }
    // pending 原始槽也算「有内容」供 UI
    if (slots.original?.status === 'pending')
        return 'original';
    if (slots.repaired?.status === 'pending')
        return 'repaired';
    return null;
}
export function getIskSlotImage(sketch, kind) {
    return getIskSlots(sketch)[kind] || null;
}
/** 当前展示用图（选用槽） */
export function getIskDisplayImage(sketch) {
    const kind = resolveIskSelectedSlot(sketch);
    if (!kind) {
        // 回退顶层
        if (!sketch)
            return null;
        if (String(sketch.imageUrl || '').trim() || String(sketch.imageOssId || '').trim()) {
            return legacyIskAsOriginalSlot(sketch);
        }
        return null;
    }
    return getIskSlotImage(sketch, kind);
}
/** 把选用槽同步回顶层兼容字段 */
export function syncIskTopLevelFromSelection(sketch) {
    const kind = resolveIskSelectedSlot(sketch);
    const slot = kind ? getIskSlotImage(sketch, kind) : null;
    if (!slot) {
        return {
            ...sketch,
            selectedSlot: sketch.selectedSlot ?? null,
        };
    }
    return {
        ...sketch,
        selectedSlot: kind,
        status: slot.status || sketch.status,
        imageUrl: slot.imageUrl ?? null,
        imageOssId: slot.imageOssId ?? null,
        model: slot.model ?? sketch.model ?? null,
        provider: slot.provider ?? sketch.provider ?? null,
        cost: Number.isFinite(Number(slot.cost)) ? Number(slot.cost) : (sketch.cost ?? null),
        generatedAt: slot.generatedAt ?? sketch.generatedAt ?? null,
        errorMessage: slot.errorMessage ?? null,
        providerJobId: slot.providerJobId ?? sketch.providerJobId ?? null,
    };
}
/** 合并写某一槽，并同步顶层 */
export function withIskSlotUpdate(sketch, kind, slot, options) {
    const base = sketch
        ? { ...sketch }
        : { status: 'idle' };
    const current = getIskSlots(base);
    const nextSlots = {
        original: kind === 'original' ? slot : current.original,
        handDrawn: kind === 'handDrawn' ? slot : current.handDrawn,
        repaired: kind === 'repaired' ? slot : current.repaired,
    };
    let selectedSlot = base.selectedSlot ?? null;
    if (options?.forceSelect && slot && iskSlotHasImage(slot)) {
        selectedSlot = kind;
    }
    else if (options?.selectIfEmpty !== false && slot && iskSlotHasImage(slot)) {
        // 若当前选用无效，或新槽优先级更高且用户未显式钉住低优先级，则按优先级解析
        const provisional = {
            ...base,
            slots: nextSlots,
            selectedSlot,
        };
        selectedSlot = resolveIskSelectedSlot(provisional);
    }
    return syncIskTopLevelFromSelection({
        ...base,
        slots: nextSlots,
        selectedSlot,
        promptUsed: base.promptUsed ?? null,
        structureBrief: base.structureBrief ?? null,
        primaryRelation: base.primaryRelation ?? null,
    });
}
/**
 * 直觉草图是否相对浓缩正文过期。
 * 有 ready 草图且 skill.contentUpdatedAt 已前进、与草图 alignedContentUpdatedAt 不一致时为 true。
 * 无 contentUpdatedAt（历史数据）→ 不过期，避免误黄。
 */
export function ipttqIskIsStale(record) {
    if (record.bankMode === 'session_originals_ref')
        return false;
    if (!ipttqHasSkillBody(record.skill))
        return false;
    if (!ipttqIskIsReady(record.skill?.intuitionSketch))
        return false;
    const contentAt = String(record.skill?.contentUpdatedAt || '').trim();
    if (!contentAt)
        return false;
    const sketch = record.skill?.intuitionSketch;
    const aligned = String(sketch?.alignedContentUpdatedAt || '').trim();
    if (aligned)
        return aligned !== contentAt;
    // 旧草图无 aligned 字段：用 generatedAt 兜底
    const display = getIskDisplayImage(sketch);
    const generatedAt = String(display?.generatedAt || sketch?.generatedAt || '').trim();
    if (!generatedAt)
        return true;
    const contentMs = Date.parse(contentAt);
    const generatedMs = Date.parse(generatedAt);
    if (!Number.isFinite(contentMs) || !Number.isFinite(generatedMs))
        return true;
    return generatedMs < contentMs;
}
/**
 * 是否缺少直觉草图或草图已过期（工具栏/详情黄灯）。
 * 平凡品题或无 skill 正文 → 不亮黄灯。
 * 注意：失败态可能仍残留旧图 URL，黄灯仍亮（须 status===ready 且未过期才灭）。
 */
export function ipttqNeedsIskWarning(record) {
    if (record.bankMode === 'session_originals_ref')
        return false;
    if (!ipttqHasSkillBody(record.skill))
        return false;
    if (!ipttqIskIsReady(record.skill?.intuitionSketch))
        return true;
    return ipttqIskIsStale(record);
}
/**
 * 预览占位 / 引导文案（无图或过期时）。
 * 有可用且未过期的图时返回 null。
 */
export function ipttqIskPlaceholderHint(record) {
    if (record.bankMode === 'session_originals_ref')
        return null;
    if (!ipttqHasSkillBody(record.skill)) {
        return '请先更新品题浓缩结果';
    }
    const sketch = record.skill?.intuitionSketch;
    const display = getIskDisplayImage(sketch);
    const slots = getIskSlots(sketch);
    const status = display?.status
        || slots.original?.status
        || sketch?.status;
    if (status === 'pending')
        return '正在生成…';
    if (status === 'failed' && !ipttqIskIsReady(sketch)) {
        return String(display?.errorMessage || sketch?.errorMessage || '').trim()
            || '生成失败，可重试生成直觉草图';
    }
    if (!ipttqIskIsReady(sketch)) {
        return '可以生成一张直觉草图试试看';
    }
    if (ipttqIskIsStale(record)) {
        return '浓缩结果已更新，可更新直觉草图（每人每日有生图限额）';
    }
    return null;
}
/** 黄灯短文案：缺图 / 失败 / 生成中 / 相对浓缩正文过期 */
export function ipttqIskWarningLabel(recordOrSketch) {
    const asRecord = recordOrSketch
        && typeof recordOrSketch === 'object'
        && 'skill' in recordOrSketch
        ? recordOrSketch
        : null;
    if (asRecord && ipttqIskIsStale(asRecord)) {
        return '黄灯：浓缩结果已更新，可更新直觉草图（每人每日有生图限额）';
    }
    const sketch = asRecord
        ? asRecord.skill?.intuitionSketch
        : recordOrSketch;
    if (!sketch)
        return '黄灯：尚无直觉草图';
    const display = getIskDisplayImage(sketch);
    const slots = getIskSlots(sketch);
    const status = display?.status
        || slots.repaired?.status
        || slots.original?.status
        || sketch.status;
    if (status === 'failed') {
        const hasImage = Boolean(String(display?.imageUrl || display?.imageOssId || sketch.imageUrl || sketch.imageOssId || '').trim());
        return hasImage
            ? '黄灯：草图未就绪（上次失败，图可能过期）'
            : '黄灯：直觉草图生成失败';
    }
    if (status === 'pending')
        return '黄灯：直觉草图生成中';
    return '黄灯：尚无直觉草图';
}
/** 是否缺少诱导题库（工具栏红灯） */
export function ipttqNeedsBankWarning(record) {
    if (record.bankMode === 'session_originals_ref') {
        if (typeof record.effectiveBankCount === 'number') {
            return record.effectiveBankCount === 0;
        }
        return !Array.isArray(record.bankParticleIds) || record.bankParticleIds.length === 0;
    }
    return !Array.isArray(record.bankParticleIds) || record.bankParticleIds.length === 0;
}
/** 平凡品题：引用会话原始颗粒作诱导题库 */
export function isIpttqSessionOriginalsRef(record) {
    return record.bankMode === 'session_originals_ref';
}
/**
 * 是否已有「完整生成」的品题浓缩结果（非平凡/不品题捷径）。
 * 用于隐藏「直接浓缩不品题」入口。
 */
export function hasCompleteGeneratedIpttq(records) {
    return records.some((record) => {
        if (isIpttqSessionOriginalsRef(record))
            return false;
        if (record.lifecycle === 'archived')
            return false;
        const caps = record.skill?.implicitCapabilities || [];
        const lineage = String(record.skill?.thinkingLineage || '').trim();
        const draftLineage = !lineage
            || lineage.includes('待根据')
            || lineage.includes('请稍后重试');
        const hasSkillBody = caps.length > 0 || (!draftLineage && lineage.length >= 12);
        if (!hasSkillBody)
            return false;
        const bankCount = typeof record.effectiveBankCount === 'number'
            ? record.effectiveBankCount
            : (record.bankParticleIds || []).length;
        return record.lifecycle === 'active' || bankCount > 0 || caps.length > 0;
    });
}
/** 列表/详情题库一行文案 */
export function formatIpttqBankMetaLine(record, options) {
    const warn = options?.warn ?? ipttqNeedsBankWarning(record);
    if (isIpttqSessionOriginalsRef(record)) {
        const n = typeof record.effectiveBankCount === 'number'
            ? record.effectiveBankCount
            : record.bankParticleIds.length;
        return `引用原始 ${n} 题${warn ? ' · 红灯：无原始颗粒' : ''} · 消费 ${record.bankConsumeCount}`;
    }
    return `题库 ${record.bankParticleIds.length}/5${warn ? ' · 红灯：无诱导题' : ''} · 消费 ${record.bankConsumeCount}`;
}
/**
 * 品题浓缩结果工具栏：颗粒已加入、有记录且无题库红灯/草图黄灯 → 就绪（绿色）。
 */
export function isIpttqToolbarReady(input) {
    if (input.particleCount <= 0)
        return false;
    if (!input.records.length)
        return false;
    return !input.records.some((item) => ipttqNeedsBankWarning(item) || ipttqNeedsIskWarning(item));
}
export const IPTTQ_SPOT_CHECK_RATIO = 5;
/** 动态择优默认权重 — cttq-domain.md §8.3 */
export const CTTQ_RESOLUTION_WEIGHTS = {
    coverage: 0.45,
    forgetting: 0.35,
    learningCost: 0.2,
    networkBoost: 0.1,
};
function clamp01(value) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.min(1, Math.max(0, value));
}
function resolveForgettingProb(membership, context) {
    const byCttq = context.forgettingProbabilityByParticleId?.[membership.cttqParticleId];
    if (typeof byCttq === 'number') {
        return clamp01(byCttq);
    }
    const byMember = context.forgettingProbabilityByParticleId?.[membership.memberParticleId];
    if (typeof byMember === 'number') {
        return clamp01(byMember);
    }
    return 0.5;
}
function resolveLearningCost(membership, record, context) {
    const fromContext = context.learningCostByParticleId?.[membership.cttqParticleId];
    if (typeof fromContext === 'number') {
        return clamp01(fromContext);
    }
    if (typeof record?.learningCostEstimate === 'number') {
        return clamp01(record.learningCostEstimate);
    }
    const chain = record?.coverageReport?.tasteDimensions?.score;
    if (typeof chain === 'number') {
        return clamp01(chain / 5);
    }
    return 0.5;
}
function resolveNetworkBoost(membership, context) {
    if (!context.preferredNetworkId) {
        return 0;
    }
    return membership.networkIds.includes(context.preferredNetworkId)
        ? CTTQ_RESOLUTION_WEIGHTS.networkBoost
        : 0;
}
function isCttqRecordReviewEligible(record) {
    if (!record) {
        return false;
    }
    const lifecycle = record.lifecycle ?? (record.status === 'active' ? 'active' : 'superseded');
    return lifecycle === 'active';
}
function resolveReviewPriorityTier(record) {
    if (!record) {
        return 'primary';
    }
    if (record.reviewPriorityTier) {
        return record.reviewPriorityTier;
    }
    const score = record.redundancyReport?.redundancyScore;
    if (typeof score === 'number' && score >= CTTQ_REDUNDANCY_DEPRIORITIZE_THRESHOLD) {
        return 'deprioritized';
    }
    return 'primary';
}
/** 计算 portfolio 收敛健康度 */
export function computeCttqPortfolioHealth(input) {
    const { subjectCode, activeCttqCount, eligibleMemberCount, totalActiveMemberships, deprioritizedCttqCount } = input;
    const convergenceRatio = eligibleMemberCount > 0
        ? activeCttqCount / eligibleMemberCount
        : 0;
    const avgMembersPerCttq = activeCttqCount > 0
        ? totalActiveMemberships / activeCttqCount
        : 0;
    const deprioritizedShare = activeCttqCount > 0
        ? deprioritizedCttqCount / activeCttqCount
        : 0;
    let alertLevel = 'ok';
    if (convergenceRatio >= CTTQ_PORTFOLIO_RATIO_CRITICAL) {
        alertLevel = 'critical';
    }
    else if (convergenceRatio >= CTTQ_PORTFOLIO_RATIO_WARN) {
        alertLevel = 'warn';
    }
    return {
        subjectCode,
        activeCttqCount,
        eligibleMemberCount,
        convergenceRatio,
        avgMembersPerCttq,
        deprioritizedShare,
        alertLevel,
    };
}
/** 创建 CTTQ 前：由冗余分推导建议动作与复习档 */
export function deriveCttqRedundancyDecision(redundancyScore, _userAcknowledgedRedundancy = false) {
    const warnings = [];
    if (redundancyScore >= CTTQ_REDUNDANCY_WARN_THRESHOLD) {
        warnings.push('与已有聚合题重叠较高，继续创建的价值相对较低。');
    }
    if (redundancyScore >= CTTQ_REDUNDANCY_DEPRIORITIZE_THRESHOLD) {
        warnings.push('复习时将不作为首要安排；建议合并到已有聚合题。');
        return {
            suggestedAction: 'proceed_deprioritized',
            reviewPriorityTier: 'deprioritized',
            warnings,
        };
    }
    return {
        suggestedAction: 'proceed_primary',
        reviewPriorityTier: 'primary',
        warnings,
    };
}
function jaccardSimilarity(a, b) {
    const setA = new Set(a);
    const setB = new Set(b);
    let intersection = 0;
    for (const id of setA) {
        if (setB.has(id)) {
            intersection += 1;
        }
    }
    const union = setA.size + setB.size - intersection;
    return union > 0 ? intersection / union : 0;
}
function isCttqRecordActiveForRedundancy(record) {
    const lifecycle = record.lifecycle ?? (record.status === 'active' ? 'active' : 'superseded');
    return lifecycle === 'active' || lifecycle === 'draft';
}
/**
 * 创建 CTTQ 前预览冗余 — member Jaccard + 可选 PAP 重叠。
 * 提示不阻断；见 cttq-domain.md §4.7.1。
 */
export function previewCttqRedundancy(input) {
    const { proposedMemberIds, existingActiveRecords, proposedNetworkIds = [], papOverlapByRecordId = {}, computedAt = new Date().toISOString(), } = input;
    const proposedNetworkSet = new Set(proposedNetworkIds);
    const overlappingRecords = [];
    for (const record of existingActiveRecords) {
        if (!isCttqRecordActiveForRedundancy(record)) {
            continue;
        }
        const memberSetJaccard = jaccardSimilarity(proposedMemberIds, record.convergedParticleIds);
        const papOverlap = papOverlapByRecordId[record.id];
        const sharedNetworks = (record.networkIds ?? []).filter((id) => proposedNetworkSet.has(id));
        const networkBoost = sharedNetworks.length > 0 ? 0.08 : 0;
        const combined = Math.max(memberSetJaccard, papOverlap ?? 0) + networkBoost;
        if (combined < CTTQ_REDUNDANCY_WARN_THRESHOLD * 0.5) {
            continue;
        }
        overlappingRecords.push({
            cttqRecordId: record.id,
            cttqParticleId: record.cttqParticleId,
            label: record.thematicLabels?.[0],
            memberSetJaccard,
            papOverlap,
        });
    }
    overlappingRecords.sort((a, b) => {
        const scoreA = Math.max(a.memberSetJaccard, a.papOverlap ?? 0);
        const scoreB = Math.max(b.memberSetJaccard, b.papOverlap ?? 0);
        return scoreB - scoreA;
    });
    const top = overlappingRecords[0];
    let redundancyScore = 0;
    if (top) {
        redundancyScore = Math.max(top.memberSetJaccard, top.papOverlap ?? 0);
        if (proposedNetworkIds.length > 0) {
            const topRecord = existingActiveRecords.find((r) => r.id === top.cttqRecordId);
            const shared = (topRecord?.networkIds ?? []).some((id) => proposedNetworkSet.has(id));
            if (shared) {
                redundancyScore = Math.min(1, redundancyScore + 0.08);
            }
        }
    }
    const decision = deriveCttqRedundancyDecision(redundancyScore);
    let suggestedAction = decision.suggestedAction;
    if (redundancyScore >= CTTQ_REDUNDANCY_DEPRIORITIZE_THRESHOLD && top) {
        suggestedAction = 'merge_into_existing';
    }
    return {
        redundancyScore,
        overlappingRecords: overlappingRecords.slice(0, 5),
        warnings: decision.warnings,
        suggestedAction,
        computedAt,
    };
}
/**
 * LEC 动态复习入口：枚举覆盖 member 的所有 CTTQ，按覆盖度/遗忘/成本择优。
 * v0.3：primary 档优先；无 primary 候选时降级选用 deprioritized（×CTTQ_DEPRIORITIZED_SCORE_FACTOR）。
 * 见 cttq-domain.md §8。
 */
export function resolveOptimalReviewEntry(memberParticleId, memberships, recordsById, context = {}, threshold = CTTQ_REVIEW_SUBSTITUTION_THRESHOLD) {
    const active = memberships.filter((m) => m.memberParticleId === memberParticleId && !m.revokedAt);
    const eligible = active.filter((m) => m.membershipCoverageScore >= threshold);
    if (eligible.length === 0) {
        return {
            entryParticleId: memberParticleId,
            memberParticleId,
            usedFallback: true,
        };
    }
    const scoreCandidate = (membership) => {
        const record = recordsById.get(membership.cttqRecordId);
        if (!isCttqRecordReviewEligible(record)) {
            return null;
        }
        const coverage = clamp01(membership.membershipCoverageScore);
        const forgetting = resolveForgettingProb(membership, context);
        const learningCost = resolveLearningCost(membership, record, context);
        const networkBoost = resolveNetworkBoost(membership, context);
        const priorityTier = resolveReviewPriorityTier(record);
        let total = CTTQ_RESOLUTION_WEIGHTS.coverage * coverage
            + CTTQ_RESOLUTION_WEIGHTS.forgetting * forgetting
            + CTTQ_RESOLUTION_WEIGHTS.learningCost * (1 - learningCost)
            + networkBoost;
        let deprioritizedPenalty = 0;
        if (priorityTier === 'deprioritized') {
            deprioritizedPenalty = total * (1 - CTTQ_DEPRIORITIZED_SCORE_FACTOR);
            total *= CTTQ_DEPRIORITIZED_SCORE_FACTOR;
        }
        const breakdown = {
            coverage,
            forgetting,
            learningCost,
            networkBoost,
            priorityTier,
            deprioritizedPenalty,
            total,
        };
        return { membership, record, breakdown, priorityTier };
    };
    const scored = eligible
        .map(scoreCandidate)
        .filter((c) => c !== null);
    if (scored.length === 0) {
        return {
            entryParticleId: memberParticleId,
            memberParticleId,
            usedFallback: true,
        };
    }
    const pickBest = (candidates) => {
        return candidates.reduce((best, current) => {
            if (current.breakdown.total > best.breakdown.total) {
                return current;
            }
            if (current.breakdown.total === best.breakdown.total
                && current.breakdown.coverage > best.breakdown.coverage) {
                return current;
            }
            return best;
        });
    };
    const primaryPool = scored.filter((c) => c.priorityTier === 'primary');
    const best = primaryPool.length > 0
        ? pickBest(primaryPool)
        : pickBest(scored);
    const chosenNetworkId = context.preferredNetworkId
        && best.membership.networkIds.includes(context.preferredNetworkId)
        ? context.preferredNetworkId
        : best.membership.networkIds[0];
    return {
        entryParticleId: best.membership.cttqParticleId,
        memberParticleId,
        chosenCttqRecordId: best.membership.cttqRecordId,
        chosenMembershipId: best.membership.id,
        chosenNetworkId,
        usedFallback: false,
        scoreBreakdown: best.breakdown,
    };
}
/**
 * @deprecated v0.2 使用 resolveOptimalReviewEntry。等价于 Wf=Wl=0 的 coverage-only 择优。
 */
export function resolveParticleForReview(sourceParticleId, substitutions, cttqRecordsByParticleId, threshold = CTTQ_REVIEW_SUBSTITUTION_THRESHOLD) {
    const memberships = substitutions
        .filter((s) => s.sourceParticleId === sourceParticleId && !s.revokedAt)
        .map((s) => ({
        id: s.id,
        userId: s.userId,
        memberParticleId: s.sourceParticleId,
        cttqRecordId: s.cttqRecordId,
        cttqParticleId: s.cttqParticleId,
        networkIds: [],
        membershipCoverageScore: cttqRecordsByParticleId.get(s.cttqParticleId)?.coverageReport.overallScore ?? 0,
        cascadeOnComplete: s.cascadeOnComplete,
        effectiveFrom: s.effectiveFrom,
        revokedAt: s.revokedAt,
    }));
    const recordsById = new Map();
    for (const m of memberships) {
        const fromSub = cttqRecordsByParticleId.get(m.cttqParticleId);
        if (fromSub) {
            recordsById.set(m.cttqRecordId, fromSub);
        }
    }
    return resolveOptimalReviewEntry(sourceParticleId, memberships, recordsById, {}, threshold).entryParticleId;
}
/** 枚举覆盖某 member 的所有 active memberships */
export function findActiveMembershipsForMember(memberParticleId, memberships) {
    return memberships.filter((m) => m.memberParticleId === memberParticleId && !m.revokedAt);
}
/** 颗粒 ID 是否为某 active CTTQ 的代表颗粒 */
export function isCttqRepresentativeParticle(particleId, records) {
    return records.some((r) => r.status === 'active' && r.cttqParticleId === particleId);
}
/** 源颗粒是否被 active CTTQ 覆盖（v0.1 substitution 兼容；v0.2 用 findActiveMembershipsForMember） */
export function findActiveSubstitutionForSource(sourceParticleId, substitutions) {
    return substitutions.find((s) => s.sourceParticleId === sourceParticleId && !s.revokedAt) ?? null;
}
function readImplicitProjection(pap) {
    const projection = pap?.implicitAbilityProjection;
    if (projection && typeof projection === 'object' && !Array.isArray(projection)) {
        return projection;
    }
    return {};
}
/** 副券展示：从 PAP 抽取 matchedTags 与 inferred steps。 */
export function buildPapStubDisplay(pap) {
    const projection = readImplicitProjection(pap);
    const rawMatched = Array.isArray(projection.matchedTags) ? projection.matchedTags : [];
    const matchedTags = rawMatched
        .map((item) => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) {
            return null;
        }
        const record = item;
        const label = String(record.label || record.implicitAbility || '').trim();
        if (!label) {
            return null;
        }
        return {
            implicitAbilityTagId: String(record.implicitAbilityTagId || '').trim() || undefined,
            label,
            category: String(record.category || '').trim() || undefined,
            matchConfidence: typeof record.matchConfidence === 'number' ? record.matchConfidence : undefined,
            reason: String(record.reason || '').trim() || undefined,
        };
    })
        .filter((item) => item !== null);
    const rawSteps = Array.isArray(projection.steps) ? projection.steps : [];
    const inferredSteps = rawSteps
        .map((item) => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) {
            return null;
        }
        const record = item;
        if (record.implicitAbilityIsNew !== true) {
            return null;
        }
        const implicitAbility = String(record.implicitAbility || '').trim();
        if (!implicitAbility) {
            return null;
        }
        return {
            stepOrder: typeof record.stepOrder === 'number' ? record.stepOrder : undefined,
            implicitAbility,
            reasoning: String(record.reasoning || '').trim() || undefined,
        };
    })
        .filter((item) => item !== null);
    return {
        matchedTags,
        inferredSteps,
        hasContent: matchedTags.length > 0 || inferredSteps.length > 0,
    };
}
export const DICTATION_PARTICLE_TAG = '[听写稿]';
function readRecord(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    return value;
}
function readStructuredMain(value) {
    if (typeof value === 'string') {
        const text = value.trim();
        if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
            try {
                return readStructuredMain(JSON.parse(text));
            }
            catch {
                return text;
            }
        }
        return text;
    }
    const record = readRecord(value);
    if (!record) {
        return '';
    }
    for (const key of ['main', 'Main', 'question', 'Question', 'text']) {
        const field = record[key];
        if (typeof field === 'string' && field.trim()) {
            return field.trim();
        }
    }
    return '';
}
/** 颗粒 Question/Answer.main 以 [听写稿] 开头（语文/英语听写训练稿）。 */
export function isDictationTaggedParticleContent(input) {
    const questionMain = readStructuredMain(input.questions);
    const answerMain = readStructuredMain(input.answers);
    return questionMain.startsWith(DICTATION_PARTICLE_TAG)
        || answerMain.startsWith(DICTATION_PARTICLE_TAG);
}
/**
 * PAP 当前仅开放数学。语文管线与数学拧巴，暂禁用；语文专用 PAP 补齐后再加回。
 */
export function isParticlePapEligibleSubject(subject) {
    const raw = String(subject ?? '').trim().toLowerCase();
    if (!raw) {
        return false;
    }
    return raw === 'math'
        || raw === 'mathematics'
        || raw === '数学';
}
/** 学科符合且非听写稿颗粒时，才参与隐性能力（PAP）匹配。 */
export function isParticlePapEligible(input) {
    if (!isParticlePapEligibleSubject(input.subject)) {
        return false;
    }
    return !isDictationTaggedParticleContent(input);
}
